import { ICharacter, useLoadCharacter, useTranslation } from "@next-platform/client-common-core";
import { Button, ButtonSize, ButtonVariant, EmptyState, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { Spinner } from "@nextui-org/react";
import { useParams } from "react-router-dom";

import { PageLayout, PageSize, Row } from "@/components";
import { ChangeConversationTitleModal, ChatsMultiSelectButtonGroup, CloneConversationModal, RemoveConversationModal } from "@/components/Chats";
import ChatCard from "@/components/Chats/ChatCard/ChatCard";
import ChatsListToolbar from "@/components/Chats/ChatsListToolbar";
import Search from "@/components/Chats/Search/Search";
import NavigateBackButtonV2 from "@/components/common/NavigateBackButtonV2";
import Trans from "@/components/Trans/Trans";
import useSavedChats from "@/hooks/useSavedChats";

export default function ChatsChatbot() {
  const { t } = useTranslation();
  const { characterId } = useParams();
  const { characterData: character } = useLoadCharacter({ characterId: characterId ?? "", withTranslation: true });
  const {
    filteredConversations,
    conversations,
    isConversationsLoading,
    isLoadingMoreConversations,
    isLoadMoreDisabled,
    isSortRefetching,
    isRemoveConversationLoading,
    onUserRemoveConversation,
    getDropdownListItems,
    selectedConversation,
    isChangeTitleModalVisible,
    setIsChangeTitleModalVisible,
    isCloneConversationModalVisible,
    setIsCloneConversationModalVisible,
    isRemoveConversationModalVisible,
    setIsRemoveConversationModalVisible,
    onUpdateConversationTitle,
    isChangeTitleLoading,
    isCloneConversationLoading,
    onUserCloneConversation,
    onSearch,
    onClearSearch,
    searchQuery,
    lastConversationId,
    loadMoreConversations,
    sortOrder,
    handleSortChange,
    multiSelectProps,
  } = useSavedChats({ characterId: characterId ?? "" });

  const {
    isMultiSelectMode,
    selectedConversationIds,
    toggleMultiSelectMode,
    toggleConversationSelection,
    selectAllConversations,
    cancelMultiSelect,
    openRemoveModalForMultiSelect,
  } = multiSelectProps;

  const noData = !filteredConversations?.length;

  // Sort refetch uses isConversationsLoading too — keep toolbar/search visible; only disable Load More.
  if (isConversationsLoading && noData && !isSortRefetching) {
    return <Spinner />;
  }
  const conversationTitle = (selectedConversation?.label || character?.name) as string;

  return (
    <PageLayout size={PageSize.Medium} className="h-full flex flex-col">
      <NavigateBackButtonV2 />
      <Row className="justify-between items-center my-lg flex-wrap gap-lg">
        <Text variant={TextVariants.HEADING_6_BOLD} className="mob:text-heading-4 font-bold">
          <Trans i18nKey="chats:page.saved.title" values={{ name: character?.name }} />
        </Text>
        <ChatsMultiSelectButtonGroup
          isMultiSelectMode={isMultiSelectMode}
          selectedCount={selectedConversationIds.size}
          totalCount={conversations?.length || 0}
          visibleCount={filteredConversations?.length || 0}
          onToggleMultiSelect={toggleMultiSelectMode}
          onSelectAll={selectAllConversations}
          onCancel={cancelMultiSelect}
          onRemoveSelected={openRemoveModalForMultiSelect}
        />
      </Row>
      {/* Search Input and Button */}
      <Search onSearch={onSearch} onClear={onClearSearch} />
      <ChatsListToolbar visibleCount={filteredConversations?.length || 0} sortOrder={sortOrder} onSortChange={handleSortChange} />
      <div className="flex-grow flex flex-col">
        {!!noData && (
          <div className="m-auto">
            <EmptyState title={t("chats:page.saved.emptyResult.title")} description={t("chats:page.saved.emptyResult.description")} />
          </div>
        )}
        <div>
          {(filteredConversations || []).map((conversation) => (
            <div key={conversation.id} className="mb-md">
              <ChatCard
                conversation={{ ...conversation, character: character as ICharacter }}
                dropdownItems={getDropdownListItems(conversation)}
                isMultiSelectMode={isMultiSelectMode}
                isSelected={selectedConversationIds.has(conversation.id)}
                onToggleSelection={toggleConversationSelection}
              />
            </div>
          ))}
        </div>
      </div>
      {!!(lastConversationId && !searchQuery) && (
        <Row justify="center" className="my-lg">
          <Button
            disabled={isLoadMoreDisabled}
            loading={isLoadingMoreConversations}
            onClick={loadMoreConversations}
            size={ButtonSize.LARGE}
            variant={ButtonVariant.OUTLINED}
          >
            <Trans i18nKey="common:loadMore" />
          </Button>
        </Row>
      )}
      {isChangeTitleModalVisible ? (
        <ChangeConversationTitleModal
          conversationTitle={conversationTitle}
          isOpen={isChangeTitleModalVisible}
          onOpenChange={setIsChangeTitleModalVisible}
          onSubmit={onUpdateConversationTitle}
          isLoading={isChangeTitleLoading}
        />
      ) : null}
      {isCloneConversationModalVisible ? (
        <CloneConversationModal
          isOpen={isCloneConversationModalVisible}
          onOpenChange={setIsCloneConversationModalVisible}
          isLoading={isCloneConversationLoading}
          onSubmit={onUserCloneConversation}
        />
      ) : null}
      {isRemoveConversationModalVisible ? (
        <RemoveConversationModal
          isLoading={isRemoveConversationLoading}
          isOpen={isRemoveConversationModalVisible}
          onOpenChange={setIsRemoveConversationModalVisible}
          onSubmit={onUserRemoveConversation}
          isMultipleMessagesSelected={isMultiSelectMode ? selectedConversationIds.size > 1 : false}
        />
      ) : null}
    </PageLayout>
  );
}
